import { useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Modal, notification } from "antd"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import { translate } from "utils/utils"
import { Route, RouteAddress } from "../../types"
import { getRouteColor } from "../../routeColors"
import {
	MovePreviewState,
	OptimizePreviewData,
	PendingMove,
	TimeDomain,
} from "./types"
import { buildTimeDomain, deliveryStops, isOutOfFormatOrder } from "./utils"
import { clearDraft, draftKey, loadDraft, saveDraft } from "./persistence"

// Local drag-preview state for the timeline; nothing hits the backend until
// "Ajustar ETAs"/"Otimizar Rotas" runs.
export function useMoveOrdersState(
	selectedRoutes: Route[],
	onSuccess: () => void,
	onPreviewChange?: (state: MovePreviewState) => void
) {
	const rawFilters = useSelector((state: any) => state.filters) || {}
	const { business, contract, date } = rawFilters

	const [stopsByRoute, setStopsByRoute] = useState<
		Record<string, RouteAddress[]>
	>({})
	const [pendingMoves, setPendingMoves] = useState<Map<string, PendingMove>>(
		new Map()
	)
	// Per-order eta override from where a stop was dropped, letting it land anywhere.
	const [positionOverrides, setPositionOverrides] = useState<
		Record<string, number>
	>({})
	const [applying, setApplying] = useState(false)
	const [previewLoading, setPreviewLoading] = useState(false)
	const [confirmingOptimize, setConfirmingOptimize] = useState(false)
	const [optimizePreview, setOptimizePreview] =
		useState<OptimizePreviewData | null>(null)
	// Clicking a pill selects it (highlighted on the sequence map); clicking
	// the same one again deselects.
	const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
	const toggleSelectOrder = (orderId: string) =>
		setSelectedOrderId((prev) => (prev === orderId ? null : orderId))

	const routeIdsKey = useMemo(
		() =>
			selectedRoutes
				.map((r) => r._id)
				.sort()
				.join(","),
		[selectedRoutes]
	)

	// Scopes the draft so it's never restored into an unrelated day's routes.
	const draftKeyValue = useMemo(() => {
		if (!business || !contract || !date) return null
		return draftKey(
			business,
			contract,
			new Date(date).toISOString().split("T")[0]
		)
	}, [business, contract, date])

	// Reseeds when the route *selection* changes, not on every re-render.
	// Restores a matching saved draft instead of starting fresh, recovering
	// in-progress edits after a refresh/crash.
	useEffect(() => {
		const fresh = Object.fromEntries(
			selectedRoutes.map((r) => [r._id, deliveryStops(r)])
		)
		const draft = draftKeyValue ? loadDraft(draftKeyValue) : null

		if (draft) {
			const selectedIds = new Set(selectedRoutes.map((r) => r._id))
			const restoredStops: Record<string, RouteAddress[]> = { ...fresh }
			Object.keys(draft.stopsByRoute).forEach((routeId) => {
				if (selectedIds.has(routeId)) {
					restoredStops[routeId] = draft.stopsByRoute[routeId]
				}
			})
			setStopsByRoute(restoredStops)
			setPendingMoves(
				new Map(
					draft.pendingMoves.filter(([, move]) =>
						selectedIds.has(move.currentRouteId)
					)
				)
			)
			// positionOverrides isn't keyed by route — drop entries for a
			// since-deselected route too, or changedCount stays > 0 for nothing.
			const restoredOrderIds = new Set(
				Object.values(restoredStops).flatMap((stops) =>
					stops.map((a) => a.order.orderId)
				)
			)
			setPositionOverrides(
				Object.fromEntries(
					Object.entries(draft.positionOverrides).filter(([orderId]) =>
						restoredOrderIds.has(orderId)
					)
				)
			)
		} else {
			setStopsByRoute(fresh)
			setPendingMoves(new Map())
			setPositionOverrides({})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [routeIdsKey, draftKeyValue])

	// onSuccess()'s refetch is async, so selectedRoutes is still stale right
	// after a commit — defers the reseed to whichever render gets the fresh prop.
	const justCommittedRef = useRef(false)
	useEffect(() => {
		if (!justCommittedRef.current) return
		justCommittedRef.current = false
		setStopsByRoute(
			Object.fromEntries(selectedRoutes.map((r) => [r._id, deliveryStops(r)]))
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedRoutes])

	// Persist on every change so a refresh/crash never loses more than the
	// in-flight drag. Once nothing is pending there's nothing worth keeping.
	useEffect(() => {
		if (!draftKeyValue) return
		if (
			pendingMoves.size === 0 &&
			Object.keys(positionOverrides).length === 0
		) {
			clearDraft(draftKeyValue)
			return
		}
		saveDraft(draftKeyValue, {
			stopsByRoute,
			pendingMoves: Array.from(pendingMoves.entries()),
			positionOverrides,
		})
	}, [draftKeyValue, stopsByRoute, pendingMoves, positionOverrides])

	// Stable color per route, assigned the first time it's seen and never
	// reassigned. Cascaded routes (optimize preview) aren't in `selectedRoutes`
	// but still need their own index, or they'd all fall back to index 0.
	const colorIndexRef = useRef<Map<string, number>>(new Map())
	selectedRoutes.forEach((route) => {
		if (!colorIndexRef.current.has(route._id)) {
			colorIndexRef.current.set(route._id, colorIndexRef.current.size)
		}
	})
	;(optimizePreview?.routes ?? []).forEach((r) => {
		if (!colorIndexRef.current.has(r.routeId)) {
			colorIndexRef.current.set(r.routeId, colorIndexRef.current.size)
		}
	})
	const routeColor = (routeId: string) =>
		getRouteColor(colorIndexRef.current.get(routeId) ?? 0)

	const timeDomain = useMemo<TimeDomain | null>(() => {
		// Every address, not just deliveries — else a depot outside the
		// delivery-only range renders under the first/last pill instead of its own spot.
		const etas = selectedRoutes
			.flatMap((r) => r.addresses || [])
			.map((a) => a.eta)
			.filter((v): v is number => !!v)
		return buildTimeDomain(etas)
	}, [selectedRoutes])

	// Only updates local state. `dropTime` is where on the target route it
	// was released.
	const handleDrop = (
		orderId: string,
		fromRouteId: string,
		toRouteId: string,
		dropTime?: number
	) => {
		if (fromRouteId === toRouteId) {
			if (dropTime != null) {
				recordReposition(orderId, fromRouteId, dropTime)
			}
			return
		}

		const commitCrossRouteMove = () => {
			setStopsByRoute((prev) => {
				const moving = prev[fromRouteId]?.find(
					(a) => a.order.orderId === orderId
				)
				if (!moving) return prev
				return {
					...prev,
					[fromRouteId]: prev[fromRouteId].filter(
						(a) => a.order.orderId !== orderId
					),
					[toRouteId]: [...(prev[toRouteId] || []), moving],
				}
			})

			setPendingMoves((prev) => {
				const next = new Map(prev)
				const existing = next.get(orderId)
				const originalRouteId = existing?.originalRouteId ?? fromRouteId
				// Dragged back to where it started — nothing left to apply for it.
				if (originalRouteId === toRouteId) {
					next.delete(orderId)
				} else {
					next.set(orderId, { originalRouteId, currentRouteId: toRouteId })
				}
				return next
			})

			if (dropTime != null) {
				setPositionOverrides((prev) => ({ ...prev, [orderId]: dropTime }))
			}
		}

		// Out-of-format box may not fit a bike — confirm before committing.
		const toRoute = selectedRoutes.find((r) => r._id === toRouteId)
		const movingAddr = stopsByRoute[fromRouteId]?.find(
			(a) => a.order.orderId === orderId
		)
		if (
			toRoute?.routeType === "bike" &&
			movingAddr &&
			isOutOfFormatOrder(movingAddr.order)
		) {
			Modal.confirm({
				title: Strings.routes.moveOrdersOutOfFormatConfirmTitle,
				content: Strings.formatString(
					Strings.routes.moveOrdersOutOfFormatConfirmContent,
					orderId
				),
				okText: Strings.routes.moveOrdersOutOfFormatConfirmOk,
				cancelText: Strings.routes.moveOrdersOptimizeCancel,
				okButtonProps: { danger: true },
				onOk: commitCrossRouteMove,
			})
			return
		}

		commitCrossRouteMove()
	}

	// Only counts as a change if it actually reorders relative to others.
	const recordReposition = (
		orderId: string,
		routeId: string,
		dropTime: number
	) => {
		const stops = stopsByRoute[routeId] || []
		const pristineTimeFor = (a: RouteAddress) => a.eta ?? Infinity
		const currentTimeFor = (a: RouteAddress) =>
			positionOverrides[a.order.orderId] ?? a.eta ?? Infinity
		const newTimeFor = (a: RouteAddress) =>
			a.order.orderId === orderId ? dropTime : currentTimeFor(a)

		const rankOf = (timeFor: (a: RouteAddress) => number) =>
			[...stops]
				.sort((a, b) => timeFor(a) - timeFor(b))
				.findIndex((a) => a.order.orderId === orderId)

		// Back to its original spot clears the flag.
		const backToOriginalSpot = rankOf(pristineTimeFor) === rankOf(newTimeFor)
		// Only a *new* reorder counts — an untouched stop shouldn't flag just
		// because another one moved past it (that one's own flag covers it).
		const isNewReorder =
			orderId in positionOverrides ||
			rankOf(currentTimeFor) !== rankOf(newTimeFor)

		setPositionOverrides((prev) => {
			if (backToOriginalSpot || !isNewReorder) {
				if (!(orderId in prev)) return prev
				const next = { ...prev }
				delete next[orderId]
				return next
			}
			return { ...prev, [orderId]: dropTime }
		})
	}

	// Includes same-route repositions too — pendingMoves alone misses those.
	const changedCount = Object.keys(positionOverrides).length

	// Current order per route (real eta or override) — source of truth for
	// payloads, Pill sequence numbers, and the map's markers.
	const orderedStops = (routeId: string): RouteAddress[] => {
		const effectiveTimeFor = (a: RouteAddress) =>
			positionOverrides[a.order.orderId] ?? a.eta ?? Infinity
		return [...(stopsByRoute[routeId] || [])].sort(
			(a, b) => effectiveTimeFor(a) - effectiveTimeFor(b)
		)
	}

	const sequenceByRoute = useMemo(() => {
		const result: Record<string, Record<string, number>> = {}
		Object.keys(stopsByRoute).forEach((routeId) => {
			result[routeId] = Object.fromEntries(
				orderedStops(routeId).map((a, i) => [a.order.orderId, i + 1])
			)
		})
		return result
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stopsByRoute, positionOverrides])

	useEffect(() => {
		onPreviewChange?.({
			stopsByRoute,
			sequenceByRoute,
			pendingMoves,
			positionOverrides,
			selectedOrderId,
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		stopsByRoute,
		sequenceByRoute,
		pendingMoves,
		positionOverrides,
		selectedOrderId,
	])

	// Only routes whose order actually changed get sent to the backend.
	const affectedRouteIds = useMemo(
		() =>
			selectedRoutes
				.filter((route) => {
					const originalIds = deliveryStops(route).map((a) => a.order.orderId)
					const currentIds = orderedStops(route._id).map((a) => a.order.orderId)
					return (
						originalIds.length !== currentIds.length ||
						originalIds.some((id, i) => id !== currentIds[i])
					)
				})
				.map((route) => route._id),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedRoutes, stopsByRoute, positionOverrides]
	)

	const buildRoutesPayload = () =>
		affectedRouteIds.map((routeId) => ({
			routeId,
			orderIds: orderedStops(routeId).map((a) => a.order.orderId),
		}))

	const clearPending = () => {
		setPendingMoves(new Map())
		setPositionOverrides({})
	}

	// Prefer the backend's own bilingual error message when present.
	const errorDescription = (response: any, fallback: string) =>
		translate(response?.data?.message) || fallback

	const handleDiscard = () => {
		setStopsByRoute(
			Object.fromEntries(selectedRoutes.map((r) => [r._id, deliveryStops(r)]))
		)
		clearPending()
	}

	// "Ajustar ETAs": keeps the exact sequence the user built and only asks
	// the backend to recalculate arrival times for it.
	const handleAdjustEtas = async () => {
		setApplying(true)
		try {
			const response = await API.put({
				url: Endpoints.v2.uriMoveOrders(),
				data: { business, contract, routes: buildRoutesPayload() },
				// Skip the global 500+ handler, which redirects to a generic error
				// page — errors here are meant to be retryable in place.
				handleError: true,
			})

			if (response.ok) {
				// Bike routes recalculated by cascade (van pickup dependency).
				const autoUpdatedRouteIds: string[] =
					response.data?.results?.autoUpdatedRouteIds ?? []
				notification.success({
					message: Strings.routes.moveOrdersTitle,
					description:
						autoUpdatedRouteIds.length > 0
							? `${Strings.routes.moveOrdersSuccess} ${Strings.formatString(
									Strings.routes.moveOrdersAutoUpdatedNote,
									autoUpdatedRouteIds.length
								)}`
							: Strings.routes.moveOrdersSuccess,
					placement: "bottomRight",
					duration: autoUpdatedRouteIds.length > 0 ? 6 : 3,
				})
				clearPending()
				justCommittedRef.current = true
				onSuccess()
			} else {
				notification.error({
					message: Strings.routes.moveOrdersTitle,
					description: errorDescription(
						response,
						Strings.routes.moveOrdersError
					),
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.routes.moveOrdersTitle,
				description: Strings.routes.moveOrdersError,
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			setApplying(false)
		}
	}

	// "Otimizar Rotas": hands the affected routes to the real optimizer, which
	// may resequence them — shown as a before/after preview before committing.
	const handlePreviewOptimize = async () => {
		setPreviewLoading(true)
		try {
			const response = await API.post({
				url: Endpoints.v2.uriPreviewRouteOptimization(),
				data: { business, contract, routes: buildRoutesPayload() },
				handleError: true, // see handleAdjustEtas
			})

			if (response.ok) {
				setOptimizePreview({
					routes: response.data?.results?.routes ?? [],
					skippedOrderIds: response.data?.results?.skippedOrderIds ?? [],
					autoUpdatedRouteIds:
						response.data?.results?.autoUpdatedRouteIds ?? [],
				})
			} else {
				notification.error({
					message: Strings.routes.moveOrdersTitle,
					description: errorDescription(
						response,
						Strings.routes.moveOrdersPreviewError
					),
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.routes.moveOrdersTitle,
				description: Strings.routes.moveOrdersPreviewError,
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			setPreviewLoading(false)
		}
	}

	const handleCancelOptimizePreview = () => setOptimizePreview(null)

	const handleConfirmOptimize = async () => {
		if (!optimizePreview) return
		setConfirmingOptimize(true)
		try {
			// Same payload as the preview (buildRoutesPayload), not the preview's
			// own placed-only orders — apply must re-attempt every order, or a
			// route the preview couldn't fully place would get saved short.
			const response = await API.post({
				url: Endpoints.v2.uriApplyRouteOptimization(),
				data: { business, contract, routes: buildRoutesPayload() },
				handleError: true, // see handleAdjustEtas
			})

			if (response.ok) {
				// Apply is all-or-nothing — success means nothing was skipped.
				const autoUpdatedRouteIds: string[] =
					response.data?.results?.autoUpdatedRouteIds ?? []

				const messageParts: (string | (string | number)[])[] = [
					Strings.routes.moveOrdersOptimizeSuccess,
				]
				if (autoUpdatedRouteIds.length > 0) {
					messageParts.push(
						Strings.formatString(
							Strings.routes.moveOrdersAutoUpdatedNote,
							autoUpdatedRouteIds.length
						)
					)
				}

				notification.success({
					message: Strings.routes.moveOrdersTitle,
					description: messageParts.join(" "),
					placement: "bottomRight",
					duration: autoUpdatedRouteIds.length > 0 ? 6 : 3,
				})
				clearPending()
				setOptimizePreview(null)
				justCommittedRef.current = true
				onSuccess()
			} else {
				notification.error({
					message: Strings.routes.moveOrdersTitle,
					description: errorDescription(
						response,
						Strings.routes.moveOrdersOptimizeError
					),
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.routes.moveOrdersTitle,
				description: Strings.routes.moveOrdersOptimizeError,
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			setConfirmingOptimize(false)
		}
	}

	return {
		stopsByRoute,
		pendingMoves,
		positionOverrides,
		sequenceByRoute,
		affectedRouteIds,
		changedCount,
		applying,
		previewLoading,
		confirmingOptimize,
		optimizePreview,
		timeDomain,
		routeColor,
		selectedOrderId,
		toggleSelectOrder,
		handleDrop,
		handleDiscard,
		handleAdjustEtas,
		handlePreviewOptimize,
		handleCancelOptimizePreview,
		handleConfirmOptimize,
	}
}
